import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BusinessDepartmentService } from '../../business-department.services';
import { DeviationServices } from '../../follow-ups/services/deviation.services';
import { RiskanalysisService } from '../../follow-ups/services/risk-analysis.service';
import { UserService } from '../../users/services/user.service';
import { SafetyRoundLookupOption } from './safety-round.models';

/** A legacy lookup row: these endpoints all answer `{ Id, Name, ... }`. */
interface LookupRow {
  Id?: number;
  Name?: string;
  ParentId?: number | null;
}

/**
 * The business lookups the round page needs, read through the same services
 * and endpoints the deviation create form uses, so a finding is registered
 * against exactly the users, departments, topics and case types a deviation
 * created by hand would offer:
 *
 *  - users       UserService.getEmployeeContactUsers        (/employeecontactusers/{businessId})
 *  - departments BusinessDepartmentService.getCachedBusinessDepartmentByBusinessId
 *                                                             (/departments/{businessId}/{roleId})
 *  - topics      RiskanalysisService.getTopicByBusinessIdForPortal (/DeviationTopic/{businessId});
 *                the uncached twin of getTopicByBusiness, whose cache ignores the id
 *  - case types  DeviationServices.getAllCaseTypeByBusinessId (/casetypes/{businessId})
 *
 * Each list fails soft to empty: a lookup that cannot load costs its dropdown,
 * not the round.
 */
@Injectable({ providedIn: 'root' })
export class FlexiSafetyRoundLookupService {
  constructor(
    private users: UserService,
    private departments: BusinessDepartmentService,
    private topics: RiskanalysisService,
    private deviations: DeviationServices,
  ) {}

  businessUsers(businessId: number): Observable<SafetyRoundLookupOption[]> {
    return this.options(this.users.getEmployeeContactUsers(businessId));
  }

  /** Top-level departments first, each followed by its sub-departments as "Parent › Child". */
  businessDepartments(businessId: number): Observable<SafetyRoundLookupOption[]> {
    return (this.departments.getCachedBusinessDepartmentByBusinessId(businessId) as Observable<LookupRow[] | null>).pipe(
      map(rows => {
        const all = (rows ?? []).filter(row => row.Id !== undefined && row.Id !== null);
        const byName = (a: LookupRow, b: LookupRow) => (a.Name ?? '').localeCompare(b.Name ?? '');
        const parents = all.filter(row => !row.ParentId).sort(byName);
        const result: SafetyRoundLookupOption[] = [];
        for (const parent of parents) {
          result.push({ label: parent.Name ?? '', value: Number(parent.Id) });
          all
            .filter(row => row.ParentId === parent.Id)
            .sort(byName)
            .forEach(child => result.push({ label: `${parent.Name ?? ''} › ${child.Name ?? ''}`, value: Number(child.Id) }));
        }
        // Children whose parent is not in the list still need to be selectable.
        all
          .filter(row => row.ParentId && !parents.some(parent => parent.Id === row.ParentId))
          .sort(byName)
          .forEach(orphan => result.push({ label: orphan.Name ?? '', value: Number(orphan.Id) }));
        return result;
      }),
      catchError(() => of([])),
    );
  }

  deviationTopics(businessId: number): Observable<SafetyRoundLookupOption[]> {
    return this.options(this.topics.getTopicByBusinessIdForPortal(businessId));
  }

  deviationCaseTypes(businessId: number): Observable<SafetyRoundLookupOption[]> {
    return this.options(this.deviations.getAllCaseTypeByBusinessId(businessId));
  }

  private options(source: Observable<unknown>): Observable<SafetyRoundLookupOption[]> {
    return (source as Observable<LookupRow[] | null>).pipe(
      map(rows => (rows ?? [])
        .filter(row => row.Id !== undefined && row.Id !== null)
        .map(row => ({ label: row.Name ?? '', value: Number(row.Id) }))),
      catchError(() => of([])),
    );
  }
}
